import { getAuth } from 'firebase/auth';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { updateWithToastPromise } from '@/clientOnlyUtils/clientUtils';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { isMacLike, useKey } from '@poseidon/components/hooks/useKey';

import { useAuthContext } from '~/auth/auth.context';
import { ADMIN_SECTIONS } from '~/components/superadmin/superadminPages';
import { SuperadminSwitchPalettePure } from '~/components/SuperadminSwitch/SuperadminSwitchPalettePure';
import type { PaletteItem, PalettePage, RecentTarget } from '~/components/SuperadminSwitch/switchPalette.model';
import {
  buildPaletteSections,
  flattenSections,
  parseRecents,
  pushRecent,
} from '~/components/SuperadminSwitch/switchPalette.model';
import { api } from '~/utils/api';

/** "⌘⇧K" on a Mac, "Ctrl+Shift+K" elsewhere — the one shortcut, shown wherever the palette is offered. */
export const switchShortcutLabel = () => (isMacLike() ? '⌘⇧K' : 'Ctrl+Shift+K');

const ALL_PAGES: PalettePage[] = ADMIN_SECTIONS.flatMap((s) =>
  s.cards.map((c) => ({ href: c.href, title: c.title, description: c.description })),
);
const DAILY_PAGES: PalettePage[] = (ADMIN_SECTIONS.find((s) => s.title === 'Daily')?.cards ?? []).map((c) => ({
  href: c.href,
  title: c.title,
  description: c.description,
}));

/** Recents are per superadmin: keyed on the signed-in Firebase uid, which stays the superadmin's while impersonating. */
const recentsKey = () => `superadmin-switch-recents:${getAuth().currentUser?.uid ?? 'anon'}`;
const readRecents = (): RecentTarget[] => {
  try {
    return parseRecents(window.localStorage.getItem(recentsKey()));
  } catch {
    return [];
  }
};
const writeRecents = (list: RecentTarget[]) => {
  try {
    window.localStorage.setItem(recentsKey(), JSON.stringify(list));
  } catch {
    // Private windows / blocked storage: recents are a convenience, never a blocker.
  }
};

/**
 * The superadmin switch palette. Mount only for superadmins — it registers ⌘⇧K (Ctrl+Shift+K) globally,
 * including while typing, and owns search, switching, recents and page jumps.
 */
export function SuperadminSwitchPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const router = useRouter();
  const [auth, { startImpersonation, stopImpersonation }] = useAuthContext();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [recents, setRecents] = useState<RecentTarget[]>([]);
  const setSearchDebounced = useDebouncedCallback(setSearch, 200);

  const toggle = useCallback(
    (e: KeyboardEvent) => {
      e.preventDefault();
      onOpenChange(!open);
    },
    [open, onOpenChange],
  );
  useKey('k', toggle, { metaWindowsCtrlKey: true, exactShift: true, allowInInputs: true });

  // Fresh palette every time it opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSearch('');
    setActiveId(null);
    setRecents(readRecents());
  }, [open]);

  const targetsQuery = api.superAdmin.switchTargets.useQuery(
    { q: search, includeInactive: showInactive || search.trim() !== '' },
    { enabled: open, placeholderData: (prev) => prev, staleTime: 60_000 },
  );
  const impersonateMutation = api.superAdmin.impersonate.useMutation();

  const impersonation = auth.impersonation;
  const sections = useMemo(
    () =>
      buildPaletteSections({
        query,
        targets: targetsQuery.data ?? [],
        recents,
        pages: ALL_PAGES,
        dailyPages: DAILY_PAGES,
        impersonating: impersonation != null,
        currentUserId: impersonation?.userId ?? null,
      }),
    [query, targetsQuery.data, recents, impersonation],
  );

  // Keep a row highlighted: the first one whenever the list changes under the cursor.
  useEffect(() => {
    const items = flattenSections(sections);
    if (items.length === 0) setActiveId(null);
    else if (!items.some((it) => it.id === activeId)) setActiveId(items[0]!.id);
  }, [sections, activeId]);

  const close = () => onOpenChange(false);

  const switchTo = async (itemId: string, target: RecentTarget) => {
    setSwitchingId(itemId);
    try {
      const data = await updateWithToastPromise({
        loading: `Switching to ${target.name}…`,
        success: `Now acting as ${target.name}`,
        mutationFnAsync: () => impersonateMutation.mutateAsync({ userId: target.userId }),
      });
      const next = pushRecent(recents, target);
      writeRecents(next);
      setRecents(next);
      startImpersonation(
        data.impersonationToken,
        target.email,
        target.userId,
        { name: data.superadminName, teamName: data.superadminTeamName },
        target.profilePicture,
      );
      close();
    } catch {
      // updateWithToastPromise already showed the error; keep the palette open to pick again.
    } finally {
      setSwitchingId(null);
    }
  };

  const onSelect = (item: PaletteItem) => {
    if (item.kind === 'back') {
      stopImpersonation();
      close();
    } else if (item.kind === 'page') {
      close();
      void router.push(item.page.href);
    } else if (item.kind === 'team') {
      void switchTo(item.id, item.switchTo);
    } else if (item.target.userId === impersonation?.userId) {
      close();
    } else {
      void switchTo(item.id, item.target);
    }
  };

  if (!open) return null;

  return (
    <SuperadminSwitchPalettePure
      query={query}
      onQueryChange={(q) => {
        setQuery(q);
        setSearchDebounced(q);
      }}
      sections={sections}
      activeId={activeId}
      onActiveIdChange={setActiveId}
      onSelect={onSelect}
      onClose={close}
      isLoading={targetsQuery.isFetching}
      switchingId={switchingId}
      showInactive={showInactive}
      onToggleInactive={() => setShowInactive((v) => !v)}
      shortcutLabel={switchShortcutLabel()}
    />
  );
}
